"use client";

import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {Controller, useForm, useWatch} from "react-hook-form";
import {useLocale, useTranslations} from "next-intl";
import {useMemo, useState, useTransition} from "react";
import {toast} from "sonner";
import {submitAppointment, type AppointmentPayload} from "@/app/actions/appointment";

export function AppointmentForm() {
  const t = useTranslations("appointment");
  const locale = useLocale() as "en" | "hi";
  const [isPending, startTransition] = useTransition();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  const schema = useMemo(
    () =>
      z.object({
        patientName: z.string().trim().min(2, t("errors.name")),
        phone: z.string().trim().regex(/^[0-9]{10}$/, t("errors.phone")),
        email: z
          .string()
          .trim()
          .optional()
          .or(z.literal(""))
          .refine((value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value), {
            message: t("errors.email"),
          }),
        date: z.string().min(1, t("errors.date")),
        slot: z.string().min(1, t("errors.slot")),
        service: z.string().min(2, t("errors.service")),
        notes: z.string().max(500).optional(),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const slots = [
    t("slots.1"),
    t("slots.2"),
    t("slots.3"),
    t("slots.4"),
    t("slots.5"),
  ];

  const services = [
    t("services.1"),
    t("services.2"),
    t("services.3"),
    t("services.4"),
    t("services.5"),
    t("services.6"),
  ];

  const {
    register,
    control,
    handleSubmit,
    formState: {errors},
    reset,
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      patientName: "",
      phone: "",
      email: "",
      date: "",
      slot: "",
      service: services[0],
      notes: "",
    },
  });

  const selectedSlot = useWatch({control, name: "slot"});

  const onSubmit = (values: FormValues) => {
    setIsSubmitting(true);

    startTransition(async () => {
      const payload: AppointmentPayload = {
        ...values,
        notes: values.notes || "",
        email: values.email || "",
        locale,
      };

      const result = await submitAppointment(payload);
      setIsSubmitting(false);

      if (result.success) {
        toast.success(t("success"));
        reset();
        window.open(`https://wa.me/919793463801?text=${result.whatsappText}`, "_blank");
      } else {
        toast.error(result.message || t("failure"));
      }
    });
  };

  return (
    <section id="appointment" className="clinic-section mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
      <div className="clinic-card p-6 md:p-9">
        <p className="clinic-eyebrow">
          {t("eyebrow")}
        </p>
        <h2 className="clinic-display clinic-heading">
          {t("title")}
        </h2>

        <form className="mt-8 grid gap-5 md:grid-cols-2" onSubmit={handleSubmit(onSubmit)}>
          <label className="grid gap-2">
            <span className="text-sm font-semibold text-[var(--clinic-ink-soft)]">{t("name")}</span>
            <input
              type="text"
              {...register("patientName")}
              className="clinic-form-control placeholder:text-[var(--clinic-muted-faint)]"
              placeholder={t("namePlaceholder")}
            />
            {errors.patientName ? (
              <span className="text-xs text-red-600">{errors.patientName.message}</span>
            ) : null}
          </label>

          <label className="grid gap-2">
            <span className="text-sm font-semibold text-[var(--clinic-ink-soft)]">{t("phone")}</span>
            <input
              type="tel"
              {...register("phone")}
              className="clinic-form-control placeholder:text-[var(--clinic-muted-faint)]"
              placeholder="9793463801"
            />
            {errors.phone ? <span className="text-xs text-red-600">{errors.phone.message}</span> : null}
          </label>

          <label className="grid gap-2">
            <span className="text-sm font-semibold text-[var(--clinic-ink-soft)]">{t("email")}</span>
            <input
              type="email"
              {...register("email")}
              className="clinic-form-control placeholder:text-[var(--clinic-muted-faint)]"
              placeholder="name@example.com"
            />
            {errors.email ? <span className="text-xs text-red-600">{errors.email.message}</span> : null}
          </label>

          <label className="grid gap-2">
            <span className="text-sm font-semibold text-[var(--clinic-ink-soft)]">{t("date")}</span>
            <input
              type="date"
              min={today}
              {...register("date")}
              className="clinic-form-control"
            />
            {errors.date ? <span className="text-xs text-red-600">{errors.date.message}</span> : null}
          </label>

          <div className="md:col-span-2">
            <Controller
              control={control}
              name="slot"
              render={() => (
                <fieldset className="grid gap-2">
                  <legend className="text-sm font-semibold text-[var(--clinic-ink-soft)]">{t("slot")}</legend>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
                    {slots.map((slot) => (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => setValue("slot", slot, {shouldValidate: true})}
                        className={`min-h-11 rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                          selectedSlot === slot
                            ? "border-[#175e52] bg-[var(--clinic-brand)] text-white shadow-sm"
                            : "border-[var(--clinic-line-strong)] bg-[#fffefa] text-[var(--clinic-ink-soft)] hover:border-[#175e52]/60 hover:bg-[#edf2ed] dark:border-white/15 dark:bg-[#102825]"
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                  {errors.slot ? <span className="text-xs text-red-600">{errors.slot.message}</span> : null}
                </fieldset>
              )}
            />
          </div>

          <label className="grid gap-2 md:col-span-2">
            <span className="text-sm font-semibold text-[var(--clinic-ink-soft)]">{t("service")}</span>
            <select
              {...register("service")}
              className="clinic-form-control"
            >
              {services.map((service) => (
                <option key={service} value={service}>
                  {service}
                </option>
              ))}
            </select>
          </label>

          <label className="grid gap-2 md:col-span-2">
            <span className="text-sm font-semibold text-[var(--clinic-ink-soft)]">{t("notes")}</span>
            <textarea
              rows={4}
              {...register("notes")}
              className="clinic-form-control placeholder:text-[var(--clinic-muted-faint)]"
              placeholder={t("notesPlaceholder")}
            />
          </label>

          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={isPending || isSubmitting}
              className="inline-flex min-h-12 items-center rounded-full bg-[var(--clinic-accent-fill)] px-7 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(201,95,75,0.18)] transition hover:-translate-y-0.5 hover:bg-[var(--clinic-accent-fill-hover)] disabled:opacity-60"
            >
              {isPending || isSubmitting ? t("submitting") : t("submit")}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
